import { getNamedDates } from 'services/utils';
import { edicaoSimplifiqueDoCiclo } from 'utils/campanha-simplifique';

/**
 * Quais cards a caixa "Ações de Engajamento" exibe, dado o ciclo e o perfil.
 *
 * As condições viviam inline no JSX de `company.tsx`, uma por card. Elas saíram para cá porque
 * a caixa precisa saber se *algum* card sobrou antes de se desenhar (HOM-01.5): com as regras
 * espalhadas, responder isso significaria repetir as nove condições num segundo lugar — dois
 * lugares para errar a cada ciclo novo.
 *
 * A lista devolvida é a fonte única: o JSX pergunta `cards.includes(...)` e a caixa inteira some
 * quando a lista vem vazia.
 */
export type EngagementCard =
  | 'fim-de-ano'
  | 'joga-junto-simplifique'
  | 'arraia'
  | 'album-bb-2026'
  | 'desafio-semestral'
  | 'desafio-lideranca'
  | 'bonus-colaboracao'
  | 'presente-lancamento'
  | 'desafios-home';

export type EngagementCardsInput = {
  /** `date_start` do ciclo selecionado. Ausente enquanto o ciclo não carregou. */
  cycleStartDate?: number;
  /** `title` do ciclo selecionado, ex.: `'1S2026'`. */
  cycleTitle?: string;
  /** Perfil rede (`user.extra.rede`) — vê o próprio card e nenhum dos demais. */
  isRede?: boolean;
  /** Regra do gestor: cadastro de missão zerado, dele ou de toda a dupla. */
  isGestor?: boolean;
  /** `has_hybrid_on_team` — pré-requisito do Bônus de Colaboração. */
  hasHybridOnTeam?: boolean;
  /** Há desafios avulsos vindos de `getChallengeHome()`. */
  hasHomeChallenges?: boolean;
};

export function visibleEngagementCards(input: EngagementCardsInput): EngagementCard[] {
  const { cycleStartDate, cycleTitle, isRede, isGestor, hasHybridOnTeam, hasHomeChallenges } = input;

  const dates = getNamedDates();
  const cards: EngagementCard[] = [];

  // O perfil rede tem uma trilha própria: nenhum card de ciclo, só o presente de lançamento.
  if (isRede) {
    cards.push('presente-lancamento');
    if (hasHomeChallenges) cards.push('desafios-home');
    return cards;
  }

  const hasCycle = typeof cycleStartDate === 'number';

  // Descontinuados a partir do 2S2026: a área encerrou os dois desafios semestrais.
  // Corte por data (e não por título) para que ciclos futuros não os ressuscitem sozinhos.
  const anteriorAo2S2026 = hasCycle && (cycleStartDate as number) < dates.ciclo_2S2026;

  if (hasCycle && cycleStartDate === dates.ciclo_2S2025) cards.push('fim-de-ano');

  // A campanha vale para todo mundo (menos rede) nos ciclos que têm edição.
  if (edicaoSimplifiqueDoCiclo(cycleStartDate)) cards.push('joga-junto-simplifique');

  if (hasCycle && cycleStartDate === dates.ciclo_1S2025) cards.push('arraia');

  if (cycleTitle === '1S2026') cards.push('album-bb-2026');

  if (anteriorAo2S2026) cards.push('desafio-semestral');

  if (isGestor && anteriorAo2S2026) cards.push('desafio-lideranca');

  if (isGestor && hasHybridOnTeam) cards.push('bonus-colaboracao');

  if (hasHomeChallenges) cards.push('desafios-home');

  return cards;
}
